import { CalendarClock, CheckCircle, X } from 'lucide-react';
import { fromZonedTime } from 'date-fns-tz';

import { ScheduleEnvironment } from '@/react/portainer/environments/update-schedules/DetailsView/types';
import { DeploymentStatus, StatusType } from '@/react/edge/edge-stacks/types';
import { TimelineDivider } from '@/react/portainer/environments/update-schedules/DetailsView/TimelineDivider';

import { Badge } from '@@/Badge';
import { Icon } from '@@/Icon';

import { formatDate, parseEnvironmentTimezone } from '../common/utils';

import { LogBox } from './LogBox';

interface Props {
  scheduleEnvironment?: ScheduleEnvironment;
}

export function UpdateTimeline({ scheduleEnvironment }: Props) {
  if (!scheduleEnvironment) {
    return null;
  }

  const edgeStackStatus =
    scheduleEnvironment.edgeStack?.Status?.[
      scheduleEnvironment.environment.Id
    ] ?? null;
  const statuses = edgeStackStatus?.Status ?? [];

  // Use the user's (browser's) timezone directly
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const environmentTimeZone = parseEnvironmentTimezone(
    scheduleEnvironment.environment.LocalTimeZone
  );
  const zonedDate = fromZonedTime(
    scheduleEnvironment?.scheduledTime ?? '',
    environmentTimeZone
  );

  const errorStatus = statuses.find(
    (status) => status.Type === StatusType.Error
  );

  return (
    <div className="ml-1 flex flex-col gap-1">
      <div className="h-12">
        <div className="mr-6 flex flex-row flex-wrap justify-between">
          <Badge type="accent" className="flex gap-1">
            <Icon icon={CalendarClock} />
            <div>Scheduled</div>
          </Badge>
          <div className="whitespace-nowrap">
            {formatDate(zonedDate, timeZone)}
          </div>
        </div>
        {statuses.length > 0 && <TimelineDivider />}
      </div>
      {statuses.map((status, i) => {
        const content = envStatus(status);
        if (!content) return null;

        return (
          <div
            key={
              scheduleEnvironment.name +
              scheduleEnvironment.environment.Id +
              status.Type +
              status.Time
            }
            className="h-12"
          >
            <div className="mr-6 flex flex-row flex-wrap justify-between">
              <div>{content}</div>
              <div className="whitespace-nowrap">
                {formatDate(new Date(status.Time * 1000))}
              </div>
            </div>
            {(errorStatus || i !== statuses.length - 1) && <TimelineDivider />}
          </div>
        );
      })}
      {errorStatus && <LogBox logs={errorStatus.Error} maxLines={7} />}
    </div>
  );
}

function envStatus(deploymentStatus: DeploymentStatus) {
  switch (deploymentStatus.Type) {
    case StatusType.Error:
      return (
        <Badge type="danger" className="flex gap-1">
          <Icon icon={X} />
          <div>Failed</div>
        </Badge>
      );
    case StatusType.RemoteUpdateSuccess:
      return (
        <Badge type="success" className="flex gap-1">
          <Icon icon={CheckCircle} />
          <div>Completed</div>
        </Badge>
      );
    case StatusType.Acknowledged:
      return (
        <Badge type="info" className="flex gap-1">
          <Icon icon={CheckCircle} />
          <div>Acknowledged</div>
        </Badge>
      );
    case StatusType.DeploymentReceived:
      return (
        <Badge type="warn" className="flex gap-1">
          <Icon icon={CheckCircle} />
          <div>Started</div>
        </Badge>
      );
    default:
      return null;
  }
}
